/**
 * taskCardAdapter — bridge between legacy task shapes (Mongo `task_*` fields,
 * legacy `PRIORITY_META` / `STATUS_META`) and the V3 design-system primitives
 * (`OrkaTaskHeader`, `OrkaTaskCard`, `OrkaTimelineBlock`).
 *
 * Single responsibility: translate a raw task into the exact props the V3
 * primitives expect, so consumer components (TaskCard, Timeline, Kanban, etc.)
 * can migrate WITHOUT touching their internal state / edit handlers.
 *
 *   const headerProps = adaptTaskToHeaderProps(task, { context: 'timeline' });
 *   <OrkaTaskHeader {...headerProps} />
 */
import {
  resolveProject,
  resolvePriority,
  resolveState,
  resolveIntent,
} from './taskIdentity';

/** Map TaskCard context prop → OrkaTaskHeader variant. */
export function variantFromContext(context) {
  if (context === 'timeline') return 'timeline';
  if (context === 'compact' || context === 'row' || context === 'list') return 'compact';
  if (context === 'mobile') return 'mobile';
  return 'board';
}

/**
 * Return canonical props for `<OrkaTaskHeader />` from a legacy task.
 * Does NOT drop anything — unknown legacy fields still pass through via
 * `task` so downstream helpers (`formatScheduled`, `formatEstimate`) work.
 */
export function adaptTaskToHeaderProps(task, opts = {}) {
  const { context, variant: variantOverride, showZones } = opts;
  const project = resolveProject(task);
  const priority = resolvePriority(task);
  const state = resolveState(task);
  const intent = resolveIntent(task);
  return {
    task: {
      // preserve legacy task so OrkaTaskHeader's own formatters keep working
      ...task,
      // canonical resolver outputs (also set preview keys for any caller that
      // uses them directly)
      projectKey: project.key,
      priority: priority.key,
      state: state.key,
      intent: intent.key,
    },
    variant: variantOverride || variantFromContext(context),
    showZones,
  };
}

/** Convenience: return just the resolved identity maps for a task. */
export function resolveTaskIdentity(task) {
  return {
    project: resolveProject(task),
    priority: resolvePriority(task),
    state: resolveState(task),
    intent: resolveIntent(task),
  };
}

/**
 * Map legacy task_status string ("To Do" / "In Progress" / "Partial" /
 * "Blocked" / "Done") → V3 state key. Used by list/table surfaces that
 * render tiny state pills without going through OrkaTaskHeader.
 */
export function legacyStatusToStateKey(status) {
  switch ((status || '').toLowerCase()) {
    case 'in progress': return 'inProgress';
    case 'partial': return 'partial';
    case 'blocked': return 'blocked';
    case 'done': return 'done';
    case 'to do':
    default:
      return 'open';
  }
}

/** Inverse — used when writing back to Mongo from a V3 picker. */
export function stateKeyToLegacyStatus(key) {
  switch (key) {
    case 'inProgress': return 'In Progress';
    case 'partial': return 'Partial';
    case 'blocked': return 'Blocked';
    case 'done': return 'Done';
    case 'open':
    default: return 'To Do';
  }
}

/** Minutes → compact estimate label ("30m", "1h 30m"). */
export function minutesToEstimateLabel(min) {
  if (min == null) return null;
  const m = Number(min);
  if (!Number.isFinite(m) || m <= 0) return null;
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}
